let controller = new AbortController()
let busy = false
const starts = new Set<() => void>()

export function getLogoutSignal(): AbortSignal {
  return controller.signal
}

export function isLoggingOut(): boolean {
  return busy
}

export function onLogoutStart(listener: () => void): () => void {
  starts.add(listener)
  return () => {
    starts.delete(listener)
  }
}

/**
 * Aborts every in-flight request carrying the signal, then arms a fresh
 * controller — without that, requests issued after the logout completes would be
 * born aborted.
 */
export function beginLogout(reason: string = 'logout'): void {
  busy = true
  starts.forEach((listener) => {
    try {
      listener()
    } catch {}
  })
  controller.abort(reason)
  controller = new AbortController()
}

export function endLogout(): void {
  busy = false
}

export function resetLogoutSignal(): void {
  controller = new AbortController()
  busy = false
}

/**
 * `AbortSignal.any` manages its own listeners, so composing needs no manual
 * teardown. Vite transpiles syntax but never polyfills runtime APIs, and this one
 * needs Safari 17.4 — on an older engine the caller keeps their own signal and
 * simply misses logout-abort, rather than every request throwing.
 */
export function withLogoutSignal(signal: AbortSignal | undefined): AbortSignal {
  const logout = getLogoutSignal()
  if (!signal) return logout

  return typeof AbortSignal.any === 'function' ? AbortSignal.any([signal, logout]) : signal
}
